import React from 'react';
import { useTranslation } from 'react-i18next';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

// FPFRONTEND: the pieces every F&P stage shares - the read-only rendering of prior stages, the
// yes/no + notes row used by the answering stages, and the small style helpers.
//
// ONE read-only renderer, used by the manager, HR, compliance and follow-up stages, so the four
// cannot drift in how a recorded answer is displayed. Each stage decides WHICH prior blocks to
// show; none of them re-implements the rendering.
//
// ITEM 8, and the reason this file exists at all: a recorded answer is ALWAYS rendered from the
// `question_text` the SERVER stored alongside it, never from a client-side copy of the question
// list. The backend stores the wording in force at the moment of signing, so an assessment
// completed before a question was reworded still reads back against the words that were actually
// attested to. `FP_CLIENT_QUESTION_FALLBACK` deliberately does not exist.

export const FP_NAVY = '#1a2744';
export const FP_GOLD = '#c9a84c';

export function fpColors(isDarkMode) {
  return {
    cardBg: isDarkMode ? '#1a2744' : '#ffffff',
    text: isDarkMode ? '#e8e4d8' : '#1a1a2e',
    muted: isDarkMode ? '#9ba8c0' : '#666',
    border: isDarkMode ? '1px solid rgba(201,168,76,0.25)' : '1px solid #e2e6ec',
    rowBorder: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f0f0f0',
    inputBg: isDarkMode ? '#0f1a30' : '#fff',
    inputBorder: isDarkMode ? '1px solid rgba(255,255,255,0.25)' : '1px solid #d0d5dd',
  };
}

// FPFIX: GET /fp-assessment/{employee_id} returns EVERY period on file (a list, newest first)
// when no period is asked for. The screens act on ONE record, so this picks it: the record for
// `period` when given, otherwise the newest. A single object (the ?period= shape) is used as is.
export function fpRecordFor(data, period) {
  if (Array.isArray(data)) {
    const list = data.filter(Boolean);
    if (period) return list.find(r => String(r.period) === String(period)) || null;
    return list[0] || null;
  }
  return data && typeof data === 'object' ? data : null;
}

// FPFIX2: the readable label of a stored status, in the reader's language. A status this screen
// does not know reads as "Status not recognised", never as its raw key.
export const FP_STATUSES = ['pending', 'submitted', 'manager_reviewed', 'hr_reviewed', 'investigation', 'completed'];
export function fpStatusLabel(t, status) {
  return t(FP_STATUSES.includes(status) ? `fp.status.${status}` : 'fp.status.unknown');
}

export function fmtFpDate(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

/** A recorded question set, read only. `rows` are the server's own stored objects:
 *  {question_id, question_text, answer, notes}. Rendered from question_text, never from any
 *  client list (item 8). */
export function FPRecordedAnswers({ title, rows, byline, isDarkMode, highlightUnexpected = false }) {
  const { t } = useTranslation();
  const c = fpColors(isDarkMode);
  const list = Array.isArray(rows) ? rows : [];
  // FPWORDING: for the reviewers, an answer other than the expected one stands out, with its note.
  const unexpected = (r) => highlightUnexpected && Boolean(r.expected_answer) && r.answer !== r.expected_answer;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
        {title}
      </div>
      {byline && (
        <div style={{ fontSize: '12px', color: c.muted, marginBottom: '8px' }}>{byline}</div>
      )}
      {list.length === 0 ? (
        <div style={{ fontSize: '13px', color: c.muted, fontStyle: 'italic' }}>Nothing recorded.</div>
      ) : (
        <div style={{ border: c.border, borderRadius: '8px', overflow: 'hidden' }}>
          {list.map((r, i) => (
            <div
              key={r.question_id || i}
              data-testid={unexpected(r) ? `fp-unexpected-${r.question_id}` : undefined}
              style={{ padding: '10px 12px', borderTop: i === 0 ? 'none' : c.rowBorder,
                       ...(unexpected(r) ? { background: isDarkMode ? 'rgba(201,168,76,0.12)' : '#FFF4E0',
                                             borderLeft: `3px solid ${FP_GOLD}` } : {}) }}
            >
              {/* Rendered from the SERVER'S stored question_text - item 8. */}
              <div style={{ fontSize: '12.5px', color: c.text, marginBottom: '4px' }}>
                {r.question_text}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: c.text }}>
                {r.answer}
              </div>
              {unexpected(r) && (
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#8A5A00', marginTop: '3px' }}>
                  {t('fp.unexpectedAnswer')} {t('fp.expectedAnswer', { answer: r.expected_answer })}
                  {!r.notes ? ` ${t('fp.noNote')}` : ''}
                </div>
              )}
              {r.notes ? (
                <div style={{ fontSize: '12.5px', color: c.muted, marginTop: '3px', whiteSpace: 'pre-wrap' }}>
                  {r.notes}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A single labelled read-only value, for the stages that record one answer rather than a set
 *  (HR's question, the compliance outcome, the follow-up). Omitted entirely when empty, so an
 *  unreached stage never renders as a blank row. */
export function FPRecordedValue({ title, value, detail, byline, isDarkMode }) {
  const c = fpColors(isDarkMode);
  if (!value && !detail) return null;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
        {title}
      </div>
      <div style={{ border: c.border, borderRadius: '8px', padding: '10px 12px' }}>
        {value ? (
          <div style={{ fontSize: '13px', fontWeight: 600, color: c.text }}>{value}</div>
        ) : null}
        {detail ? (
          <div style={{ fontSize: '12.5px', color: c.muted, marginTop: '3px', whiteSpace: 'pre-wrap' }}>{detail}</div>
        ) : null}
        {byline ? (
          <div style={{ fontSize: '12px', color: c.muted, marginTop: '6px' }}>{byline}</div>
        ) : null}
      </div>
    </div>
  );
}

/** One answerable question: a Yes/No pair plus its own optional notes field. Used by the employee
 *  stage (twelve of these) and the manager stage (three), so the two cannot diverge in behaviour.
 *  Notes are OPTIONAL here by design - the backend refuses only a missing or non-Yes/No answer. */
export function FPQuestionRow({ index, questionId, questionText, value, notes, onChange, isDarkMode, disabled,
                               expectedAnswer }) {
  const { t } = useTranslation();
  const c = fpColors(isDarkMode);
  // FPWORDING: an answer other than the expected one needs its note (the server refuses it without).
  const noteRequired = Boolean(expectedAnswer) && Boolean(value) && value !== expectedAnswer;
  return (
    <div style={{ padding: '12px', border: c.border, borderRadius: '8px', marginBottom: '10px' }}>
      <div style={{ fontSize: '12.5px', color: c.text, marginBottom: '8px' }}>
        <span style={{ color: c.muted, marginRight: '6px' }}>{index}.</span>
        {questionText}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        {['Yes', 'No'].map(opt => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              disabled={disabled}
              aria-pressed={active}
              aria-label={`${questionId} ${opt}`}
              onClick={() => onChange({ answer: opt })}
              style={{
                background: active ? FP_NAVY : 'transparent',
                color: active ? '#fff' : c.text,
                border: active ? `1px solid ${FP_NAVY}` : c.border,
                borderRadius: '6px', padding: '6px 18px', fontSize: '13px',
                fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {noteRequired && (
        <div role="note" data-testid={`fp-note-required-${questionId}`}
             style={{ fontSize: '12px', color: '#8A5A00', marginBottom: '6px' }}>
          {t('fp.unexpectedNote')}
        </div>
      )}
      <textarea
        value={notes || ''}
        disabled={disabled}
        onChange={e => onChange({ notes: e.target.value })}
        maxLength={2000}
        rows={2}
        placeholder={noteRequired ? t('fp.noteRequired') : 'Notes (optional)'}
        aria-required={noteRequired ? 'true' : undefined}
        aria-label={`${questionId} notes`}
        style={{
          width: '100%', boxSizing: 'border-box', fontSize: '13px', padding: '7px 9px',
          borderRadius: '6px', border: c.inputBorder, background: c.inputBg,
          color: c.text, fontFamily: 'inherit', resize: 'vertical',
        }}
      />
    </div>
  );
}

/** The submit button every stage uses, so the four stages share one affordance. */
export function FPSubmitButton({ onClick, busy, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      style={{
        background: FP_NAVY, color: '#fff', border: 'none', borderRadius: '6px',
        padding: '9px 18px', fontSize: '13px', fontWeight: 600,
        cursor: (busy || disabled) ? 'not-allowed' : 'pointer',
        opacity: (busy || disabled) ? 0.6 : 1,
      }}
    >
      {busy ? 'Saving…' : children}
    </button>
  );
}

// FPSWITCH: while FP_ENABLED is off, these roles see a short notice where the F&P screens would be.
// Every other viewer sees nothing at all. The backend is the real gate (it refuses a submission
// with fp_not_active); this only decides what is shown.
export const FP_NOTICE_ROLES = ['coo', 'admin', 'compliance'];

// FPPREVIEW: the banner on every F&P screen of an FP_PREVIEW_EMAILS account while F&P is off.
export function FPPreviewBanner({ text, isDarkMode }) {
  const c = fpColors(isDarkMode);
  return (
    <div role="status" data-testid="fp-preview"
         style={{ fontSize: '13px', fontWeight: 600, color: c.text, border: `1px solid ${FP_GOLD}`,
                  background: isDarkMode ? 'transparent' : '#FFF8EC', borderRadius: '8px',
                  padding: '10px 12px', marginBottom: '12px' }}>
      {text}
    </div>
  );
}

export function FPNotActiveNotice({ text, isDarkMode }) {
  const c = fpColors(isDarkMode);
  return (
    <div role="status" data-testid="fp-not-active"
         style={{ fontSize: '13px', color: c.muted, border: c.border, borderRadius: '8px',
                  padding: '10px 12px', marginBottom: '12px' }}>
      {text}
    </div>
  );
}

// FPSTEPOWNER: the server's refusal of a step that is not the caller's, translated.
export const FP_NOT_YOUR_STEP_DETAIL = 'This step of the assessment is not yours to complete.';
export function fpStepRefusal(detail, t) {
  return detail === FP_NOT_YOUR_STEP_DETAIL ? t('fp.notYourStep') : null;
}

// FPTWOROLES: the note shown when one person completed two review stages (no one else held the role).
export const FP_SAME_PERSON_PAIRS = ['manager_hr', 'manager_compliance', 'hr_compliance'];
export function fpSamePersonNotes(record, t) {
  const pairs = Array.isArray(record?.same_person) ? record.same_person : [];
  return pairs.filter(p => FP_SAME_PERSON_PAIRS.includes(p)).map(p => t(`fp.samePerson.${p}`));
}

// FPHRVALIDATION: the assessment an F&P review task belongs to (its /open/fpreview/<id> link), or null.
export const FP_REVIEW_TASK_TYPES = ['fp_manager_review', 'fp_hr_review', 'fp_compliance_review', 'fp_investigation_followup'];
export function fpAssessmentOfTask(task) {
  if (!task || !FP_REVIEW_TASK_TYPES.includes(task.task_type)) return null;
  const m = /^\/open\/fpreview\/([A-Za-z0-9-]+)$/.exec(String(task.link_url || ''));
  return m ? m[1] : null;
}
